/**
 * Matchmaking API utility functions
 *
 * Communicates with the matchmaking service through the bQuest backend proxy
 * (BFF). The backend forwards the request to the Care Engine server-side, so
 * the agent URL and vector store id never reach the browser bundle.
 */

import { apiClient } from '@/core/api/client';
import { createMatchmakingProxyPayload } from './search-payload.utils';
import type { ApiResponse, ApiMatchResult } from './matchmakingHelpers';
import type { StepperAnswers } from '../hooks/useMatchmakingStateManagement';

const GET_MATCHES_ENDPOINT = '/professionals/matchmaking/get-matches';

/**
 * Parameters for calling the matchmaking API
 */
type CallMatchmakingApiParams = {
  matchingPrompt: string;
  filterValues: string | null;
  answersToUse: StepperAnswers;
};

/**
 * Calls the backend matchmaking proxy to get provider matches
 *
 * Sends the search prompt, filter values, and stepper answers to the backend,
 * which performs the semantic search server-side and returns matched providers.
 *
 * @param params - API call parameters
 * @returns Promise resolving to API response with matched providers
 * @throws Error if the request fails or the response is invalid
 */
export const callMatchmakingApi = async (
  params: CallMatchmakingApiParams,
): Promise<ApiResponse> => {
  const { matchingPrompt, filterValues, answersToUse } = params;

  const payload = createMatchmakingProxyPayload(matchingPrompt, filterValues, answersToUse);

  const response = await apiClient.post<ApiResponse>(GET_MATCHES_ENDPOINT, payload);
  const data = response.data;

  if (!data.result || typeof data.result !== 'object') {
    throw new Error('Matchmaking proxy response was not in the expected format.');
  }

  return data;
};

/**
 * Handles matchmaking response with status code 204 (no results)
 *
 * @param matchmakingApiData - API response data
 * @returns Default message for no results scenario
 */
export const handleNoResultsResponse = (matchmakingApiData: ApiResponse): string => {
  return (
    (matchmakingApiData as any)?.result?.message ||
    'No suitable providers found. Please refine your search.'
  );
};

/**
 * Extracts match results record from API response
 *
 * @param matchmakingApiData - API response data
 * @returns Record of match results keyed by rank
 */
export const extractMatchResults = (matchmakingApiData: ApiResponse): Record<string, ApiMatchResult> => {
  return matchmakingApiData.result as Record<string, ApiMatchResult>;
};
